import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, Clock, Hourglass, MonitorPlay, Users, CheckCircle2, Target, BookOpen, Sparkles, UserCheck, ShieldCheck } from "lucide-react";
import { Reveal, Spinner, Empty } from "@/components/site/Bits";
import { SeatsBadge } from "@/components/site/CourseCard";
import { publicApi, fileUrl, fmtDate, fmtPrice, fmtTime } from "@/lib/api";
import { usePageTitle } from "@/lib/site";

const Block = ({ Icon, title, items, numbered, testId }) =>
  items?.length ? (
    <Reveal>
      <section className="card-lux !transform-none p-7" data-testid={testId}>
        <h2 className="font-display text-xl font-bold mb-5 flex items-center gap-2"><Icon className="text-[var(--copper)]" size={22} />{title}</h2>
        <ul className="space-y-3">
          {items.map((x, i) => (
            <li key={i} className="flex gap-3 items-start text-[var(--ink)]">
              {numbered ? <span className="shrink-0 w-7 h-7 rounded-full bg-[#D4AF37]/15 text-[#8a6a10] text-sm font-bold grid place-items-center">{i + 1}</span> : <CheckCircle2 size={18} className="shrink-0 mt-1 text-[var(--gold)]" />}
              <span className="leading-relaxed">{x}</span>
            </li>
          ))}
        </ul>
      </section>
    </Reveal>
  ) : null;

const Row = ({ Icon, label, value, testId }) => (
  <div className="flex items-center justify-between py-3 border-b border-[var(--line)] last:border-0" data-testid={testId}>
    <span className="flex items-center gap-2 text-[var(--muted-ink)] text-sm"><Icon size={16} className="text-[var(--copper)]" />{label}</span>
    <span className="font-semibold text-sm text-left">{value}</span>
  </div>
);

const Sidebar = ({ c }) => (
  <aside className="card-lux !transform-none p-7 lg:sticky lg:top-28" data-testid="course-sidebar">
    <p className="text-sm text-[var(--muted-ink)]">رسوم الدورة</p>
    <p className="font-display text-4xl font-black gold-text mt-1" data-testid="course-detail-price">{fmtPrice(c.price)}</p>
    <div className="mt-5">
      <Row Icon={CalendarDays} label="التاريخ" value={fmtDate(c.date)} testId="course-detail-date" />
      <Row Icon={Clock} label="الوقت" value={fmtTime(c.time)} testId="course-detail-time" />
      <Row Icon={Hourglass} label="المدة" value={c.duration_label} testId="course-detail-duration" />
      <Row Icon={MonitorPlay} label="طريقة التدريب" value={c.mode} testId="course-detail-mode" />
      <Row Icon={Users} label="عدد المقاعد" value={`${c.seats_total} مقعداً`} testId="course-detail-seats-total" />
      <Row Icon={UserCheck} label="المقاعد المتبقية" value={<SeatsBadge course={c} />} testId="course-detail-seats-left" />
    </div>
    {c.can_register ? (
      <Link to={`/enroll/${c.id}`} className="btn-copper w-full mt-6" data-testid="course-detail-register-btn">سجّل الآن واحجز مقعدك</Link>
    ) : (
      <button disabled className="btn-copper w-full mt-6" data-testid="course-detail-register-btn">{c.seats_left <= 0 ? "اكتملت المقاعد" : "التسجيل مغلق"}</button>
    )}
    <p className="mt-4 text-xs text-[var(--muted-ink)] flex items-center gap-1.5"><ShieldCheck size={14} className="text-[var(--gold)]" />دفع إلكتروني آمن بالريال العماني</p>
  </aside>
);

export default function CourseDetail() {
  const { ref } = useParams();
  const [c, setC] = useState(undefined);
  useEffect(() => {
    publicApi.get(`/courses/${ref}`).then((r) => setC(r.data)).catch(() => setC(null));
  }, [ref]);
  usePageTitle(c?.title || "تفاصيل الدورة");
  if (c === undefined) return <Spinner />;
  if (c === null) return <div className="container-x py-20"><Empty text="الدورة غير موجودة" /></div>;
  return (
    <div data-testid="course-detail-page">
      <section className="relative h-[46vh] min-h-[320px] overflow-hidden">
        <img src={fileUrl(c.image) || "/images/course-ai.jpg"} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F1815] via-[#1F1815]/60 to-transparent" />
        <div className="container-x relative h-full flex flex-col justify-end pb-10">
          <span className="self-start bg-[var(--gold)] text-[var(--ink)] text-xs font-bold px-3 py-1 rounded-full mb-4">{c.category}</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-snug max-w-4xl" data-testid="course-detail-title">{c.title}</h1>
          {c.tagline && <p className="mt-4 text-[#F7E8A4] text-base md:text-lg max-w-3xl flex gap-2"><Sparkles size={20} className="shrink-0 mt-1" />{c.tagline}</p>}
        </div>
      </section>
      <div className="container-x py-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <Reveal>
            <section className="card-lux !transform-none p-7" data-testid="course-description">
              <h2 className="font-display text-xl font-bold mb-4">عن الدورة</h2>
              <p className="leading-loose text-[var(--muted-ink)] whitespace-pre-line">{c.description}</p>
            </section>
          </Reveal>
          <Block Icon={Target} title="الأهداف التعليمية" items={c.objectives} testId="course-objectives" />
          <Block Icon={BookOpen} title="محتويات الدورة" items={c.contents} numbered testId="course-contents" />
          <Block Icon={Sparkles} title="المهارات المكتسبة" items={c.skills} testId="course-skills" />
          <Block Icon={CheckCircle2} title="مميزات الدورة" items={c.features} testId="course-features" />
          <Block Icon={UserCheck} title="الفئة المستهدفة" items={c.audience} testId="course-audience" />
        </div>
        <div className="lg:col-span-4 order-first lg:order-none"><Sidebar c={c} /></div>
      </div>
    </div>
  );
}
